import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";

/**
 * Storefront Seller Agreement — APPROVED FINAL (May 26, 2026).
 *
 * The contract a kennel accepts when they claim a /store/{slug}.
 * Designed to:
 *   1. Make the Seller the merchant-of-record (no marketplace liability)
 *   2. Define prohibited products clearly so suspension decisions are
 *      defensible
 *   3. Document the deactivation / appeal flow Stripe sometimes
 *      requires us to demonstrate
 *   4. Pass through Stripe Connect's own restricted-businesses
 *      obligations to every Seller
 *
 * Linked from the slug-claim form's required acceptance checkbox and
 * from §5b of the main Terms of Service. Section numbering follows the
 * approved copy — do not renumber without legal review.
 */
export default function StorefrontSellerAgreement() {
  return (
    <LegalLayout
      eyebrow="Legal · Sellers"
      title="Storefront Seller Agreement"
      lastUpdated="February 28, 2026"
    >
      <p>
        This Storefront Seller Agreement (&ldquo;Agreement&rdquo;) is between
        you (&ldquo;Seller&rdquo;) and Armstrong K9 Registry
        (&ldquo;Registry&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). By
        claiming a storefront slug and listing any product, Seller accepts
        this Agreement. It is in addition to &mdash; and incorporated by
        reference into &mdash; the Registry&rsquo;s main{" "}
        <Link to="/terms">Terms of Service</Link> and{" "}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <div
        style={{
          margin: "16px 0 24px",
          padding: "14px 18px",
          border: "2px solid #C0392B",
          background: "#FDECEC",
          borderRadius: 6,
          fontSize: "0.95rem",
          lineHeight: 1.55,
        }}
        data-testid="storefront-venue-banner"
      >
        <p style={{ margin: 0, fontWeight: 700, color: "#7A1F12" }}>
          VENUE POSTURE
        </p>
        <p style={{ margin: "8px 0 0", color: "#3B0E07" }}>
          Armstrong K9 Registry is a <strong>venue</strong> connecting Sellers
          with end customers. Armstrong is <strong>not</strong> the seller,
          manufacturer, distributor, packager, shipper, or guarantor of any
          product offered through your storefront. Seller is the merchant of
          record for every transaction and assumes all product, regulatory,
          tax, and customer-service liability. Customers buy from your
          storefront <strong>at their own risk</strong>; Armstrong has no
          duty to refund, replace, or compensate any customer for any product
          you sell.
        </p>
      </div>

      <h2>1. Merchant-of-record status</h2>
      <p>
        Seller is the sole merchant of record for every transaction processed
        through their <code>/store/&#123;slug&#125;</code> page. Seller &mdash;
        not the Registry &mdash; is the seller, manufacturer, distributor,
        packager, and shipper of all goods. Seller is responsible for:
      </p>
      <ul>
        <li>
          Product safety, ingredients, labeling, and regulatory compliance
          (FDA, USDA, AAFCO, state-feed, EPA, and applicable
          consumer-protection laws);
        </li>
        <li>
          Pricing accuracy, in-stock representation, and honest product
          descriptions;
        </li>
        <li>
          Fulfillment and delivery within the Seller&rsquo;s declared
          delivery zone or shipping policy;
        </li>
        <li>
          Customer support, refunds, replacements, and dispute handling;
        </li>
        <li>
          <strong>Sales tax collection &amp; remittance.</strong> The
          Registry uses Stripe Tax to{" "}
          <em>calculate and collect</em> sales tax on your behalf at
          checkout for Dog Shop orders, transferred to your Stripe
          Connect account alongside the order total. You remain{" "}
          <strong>solely responsible</strong> for: (a) registering and
          maintaining nexus in every jurisdiction where you have a
          collection obligation; (b) remitting the collected tax to the
          appropriate authorities via your Stripe Tax dashboard; (c)
          filing returns; and (d) handling any audit, refund, or
          mis-calculation dispute. The Registry's role is limited to
          assisting with point-of-sale calculation; it is not a tax
          advisor, tax preparer, or remittance agent.
        </li>
        <li>
          Income tax on net sales; the Registry will not issue 1099-Ks
          (Stripe will, if applicable).
        </li>
      </ul>

      <h2>2. Stripe Connect requirements</h2>
      <ul>
        <li>
          Seller must complete and maintain a Stripe Connect Express account
          with payouts enabled. Loss of payout eligibility automatically
          suspends the Storefront.
        </li>
        <li>
          Seller agrees to Stripe&rsquo;s Connected Account Agreement and
          Restricted Businesses policies, which take precedence over this
          Agreement where conflicting.
        </li>
        <li>
          The Registry retains a <strong>3% application fee</strong> on every
          charge, deducted automatically by Stripe at the moment of payment.
          Stripe also charges its own processing fee (currently 2.9% + $0.30
          in the United States).
        </li>
        <li>
          Stripe &mdash; not the Registry &mdash; controls payout timing,
          holds, reserves, and any fund-availability decisions.
        </li>
      </ul>

      <h2>3. Prohibited products</h2>
      <p>
        The following may not be listed or sold on any Storefront:
      </p>
      <ul>
        <li>
          <strong>Dog-related services of any kind</strong> &mdash; including
          but not limited to training (obedience, protection,
          behavior-modification), boarding, daycare, grooming, dog walking,
          professional handling, and in-person consults. Storefronts are
          limited to <em>physical goods</em> (food, treats, supplements,
          merch). Services carry bite-injury, professional-liability, and
          state-licensing tails that the Registry, as a marketplace, cannot
          insure or warrant. Sellers offering training should use a
          separately-incorporated entity (for example, Supreme Team K9) with
          its own general-liability and professional-liability coverage;
        </li>
        <li>Human or veterinary prescription medications;</li>
        <li>Products containing CBD, hemp, kratom, or THC;</li>
        <li>Live animals (use the dog marketplace);</li>
        <li>Raw or temperature-controlled meat shipped across state lines;</li>
        <li>
          Counterfeit, pirated, replica, or unauthorized brand-merchandise;
        </li>
        <li>
          Firearms, ammunition, electronic training collars that are illegal
          in the Seller&rsquo;s state, and any product whose lawful sale
          requires a license the Seller does not hold;
        </li>
        <li>
          Anything requiring FDA, EPA, USDA, or DEA registration the Seller
          does not actively hold;
        </li>
        <li>Anything appearing on the Stripe Restricted Businesses list;</li>
        <li>
          Any product, treatment, supplement, or device that makes
          unsubstantiated medical, curative, or diagnostic claims.
        </li>
      </ul>

      <h2>4. Conduct standards</h2>
      <ul>
        <li>
          No deceptive product photos, fake reviews, or astroturfed
          endorsements.
        </li>
        <li>
          No bait-and-switch pricing or hidden fees added after checkout.
        </li>
        <li>
          Respond to buyer support inquiries within <strong>five (5) business
          days</strong>.
        </li>
        <li>
          Ship or hand-deliver in-zone food orders within <strong>seven (7)
          calendar days</strong> of payment unless the product page clearly
          states a longer lead time.
        </li>
        <li>
          Maintain a chargeback rate <strong>below 1%</strong> of monthly
          transactions, as measured by Stripe.
        </li>
        <li>
          Do not use the Storefront page or buyer contact info to spam,
          harass, or solicit unrelated business.
        </li>
      </ul>

      <h2>5. Disputes, refunds, and chargebacks</h2>
      <ul>
        <li>
          Refunds are issued by Seller from their Stripe balance. The
          Registry&rsquo;s 3% application fee is non-refundable on full
          refunds (Stripe processing fees are likewise retained by Stripe).
        </li>
        <li>
          Chargebacks are handled directly between the buyer, Seller, and
          Stripe. The Registry will not represent or mediate but may provide
          non-binding context on request.
        </li>
        <li>
          Seller indemnifies and holds harmless the Registry from any claim,
          demand, or expense arising from a Storefront transaction &mdash;
          including, but not limited to, ingredient claims, illness, injury,
          property damage, IP infringement, or regulatory action.
        </li>
      </ul>

      <h2>6. Suspension, deactivation, and removal</h2>
      <p>
        The Registry may, at its sole discretion and without prior notice:
      </p>
      <ul>
        <li>
          Suspend a Storefront (hide it from the public directory and pause
          checkout) for any suspected violation of this Agreement, the main{" "}
          <Link to="/terms">Terms of Service</Link>, applicable law, or the
          Stripe Restricted Businesses policy;
        </li>
        <li>
          Cancel all pending orders tied to the suspended Storefront; Seller
          is responsible for refunding any orders already captured in Stripe;
        </li>
        <li>
          Pause Stripe payouts to the connected account during suspension
          while the Registry investigates;
        </li>
        <li>
          Permanently delete a Storefront for repeated violations, irreparable
          harm to buyers, fraud, or Stripe account termination.
          Buyers&rsquo; historical order receipts remain visible to them.
        </li>
      </ul>
      <p>
        Suspended Sellers may appeal in writing to{" "}
        <a href="mailto:registry@armstrongk9registry.com">registry@armstrongk9registry.com</a>{" "}
        within <strong>fourteen (14) calendar days</strong>. The
        Registry&rsquo;s appeal decision is final.
      </p>

      <h2>7. Intellectual property</h2>
      <ul>
        <li>
          Seller retains all rights to their product photos, descriptions, and
          brand assets uploaded to the Storefront.
        </li>
        <li>
          Seller grants the Registry a non-exclusive, worldwide, royalty-free
          licence to display those assets in connection with operating the
          Storefront, the public directory, search results, and social-share
          previews.
        </li>
        <li>
          Seller represents and warrants that every uploaded asset is either
          owned by Seller or properly licensed.
        </li>
      </ul>

      <h2>8. Subscription, fees, and termination</h2>
      <ul>
        <li>
          The Dog Shop subscription ($19/month or $190/year, formerly
          &ldquo;Kennel Storefront&rdquo;) is billed in advance and is
          non-refundable. Cancelling stops future billing but does not refund
          the current period; the Storefront remains active until the period
          ends.
        </li>
        <li>
          The optional $9/month Featured rotation slot is a one-time 30-day
          promotional placement and is non-refundable.
        </li>
        <li>
          Either party may terminate this Agreement at any time. Sellers may
          initiate termination or cancellation by submitting a written notice
          via email to{" "}
          <a href="mailto:registry@armstrongk9registry.com">
            registry@armstrongk9registry.com
          </a>
          . Termination by Seller does not cancel pending Stripe-side payouts,
          disputes, or chargebacks already in flight.
        </li>
      </ul>

      <h2>9. Changes to this Agreement</h2>
      <p>
        We may update this Agreement from time to time. Material changes will
        be highlighted on this page and emailed to the Seller&rsquo;s account
        email at least <strong>seven (7) days</strong> before they take
        effect. Continued use of the Storefront constitutes acceptance of the
        updated Agreement.
      </p>

      <h2>10. Contact</h2>
      <p>
        Storefront support:{" "}
        <a href="mailto:registry@armstrongk9registry.com">registry@armstrongk9registry.com</a>.
      </p>
    </LegalLayout>
  );
}
